import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { useDisplayPrice } from "@majourneys/currency";
import type { AiMatch, Itinerary, ItineraryDay } from "../api/ai.api";
import type { ChatLang } from "../hooks/use-ai-chat";
import { activityHrefString } from "../../activities/lib/activity-href";
import { panelStyle } from "../../../shared/ui/card-frame";
import { LayoutLabel } from "./layout-parts";
import { CHOSEN_STYLE, CONTROL_STYLE, SOFT } from "./soft-tokens";
import { kindLabel } from "../lib/kind-label";
import { planDayLabel, planSlotLabel, type PlanLabel } from "@majourneys/shared";
import type { TFunction } from "i18next";

/**
 * The API writes "Day 3" and "Morning" in English, because they are also its
 * machine keys. Shown in the guest's language here; anything else as written.
 */
function planText(t: TFunction, label: PlanLabel | null, raw: string): string {
  if (!label) return raw;
  const key = label.key === "day" ? "ai_concierge.plan_day" : `ai_concierge.plan_${label.key}`;
  return t(key, { ...(label.params ?? {}) }) as string;
}
const dayText = (t: TFunction, raw: string) => planText(t, planDayLabel(raw), raw);
const slotText = (t: TFunction, raw: string) => planText(t, planSlotLabel(raw), raw);

/**
 * Chat kit C9 + C25, the itinerary, shared by the homepage modal and the
 * Discover chat (ai-chat-parity rule: one change ships to both).
 *
 *  - One day: a timeline. Numbered stops down a hairline, the time of day in
 *    small caps, the place, its line, and the travel note between two stops.
 *  - Several days: day tabs over the same timeline, with ONE highlight that
 *    slides between the tabs by transform (the motion kit's "one sliding
 *    highlight"), never a background on each tab.
 *
 * A stop we sell is a link to its page. A stop we do not sell (a route stop
 * in a city the catalogue is thin in) is still drawn, as text only: no photo,
 * no link, nothing that makes a place we cannot arrange look like one we can.
 */
export function ItineraryView({
  itinerary,
  matches,
  onAddToTrip,
  activeTripName,
  lang,
}: {
  itinerary: Itinerary;
  matches: AiMatch[];
  onAddToTrip?: (mode: "new" | "active") => void;
  activeTripName?: string | null;
  /** Locale for the stop links. Falls back to the i18n language. */
  lang?: ChatLang;
}) {
  const { t, i18n } = useTranslation();
  const displayPrice = useDisplayPrice();
  const linkLang = lang ?? (i18n.language?.split("-")[0] || "en");
  const matchBySlug = new Map(matches.map((m) => [m.slug, m]));
  const days = itinerary.days;
  const [dayIndex, setDayIndex] = useState(0);
  const current = days[Math.min(dayIndex, days.length - 1)];
  const multi = days.length > 1;
  const tabsId = useId();

  if (!current) return null;

  const facts = [
    itinerary.cities && itinerary.cities.length > 0 ? itinerary.cities.join(" · ") : null,
    itinerary.totalHours != null
      ? t("ai_concierge.itinerary_hours", { defaultValue: "~{{count}} h", count: itinerary.totalHours })
      : null,
    itinerary.totalPriceMad != null && itinerary.totalPriceMad > 0
      ? displayPrice(itinerary.totalPriceMad)
      : null,
  ].filter((f): f is string => !!f);

  return (
    <section style={{ ...panelStyle(18, "#fff"), padding: 12 }} data-testid="itinerary-card">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <LayoutLabel>
          {t("ai_concierge.itinerary_days", { defaultValue: "{{count}} days", count: days.length })}
        </LayoutLabel>
        {facts.length > 0 ? (
          <span className="text-[11.5px] tabular-nums" style={{ color: SOFT.body }}>
            {facts.join(" · ")}
          </span>
        ) : null}
      </div>

      {multi ? (
        <DayTabs
          days={days}
          value={dayIndex}
          onChange={setDayIndex}
          idBase={tabsId}
          label={t("ai_concierge.itinerary_tabs", { defaultValue: "Days of your trip" })}
        />
      ) : null}

      <div
        key={dayIndex}
        className="mj-fade-in mt-3"
        role={multi ? "tabpanel" : undefined}
        id={multi ? `${tabsId}-panel` : undefined}
        aria-labelledby={multi ? `${tabsId}-tab-${dayIndex}` : undefined}
        data-testid="itinerary-day"
      >
        <DayHeader day={current} />
        <Timeline day={current} matchBySlug={matchBySlug} lang={linkLang} />
      </div>

      {onAddToTrip ? (
        <AddToTrip onAddToTrip={onAddToTrip} activeTripName={activeTripName} />
      ) : null}
    </section>
  );
}

function DayHeader({ day }: { day: ItineraryDay }) {
  const { t } = useTranslation();
  // Only the stops the timeline can draw, so the count matches the list.
  const stops = day.slots.filter((s) => s.matchSlug || s.place).length;
  return (
    // The day, date and city wrap instead of cutting: the date is what the guest plans by.
    <div className="mb-2 flex items-start justify-between gap-2">
      <span className="min-w-0 break-words text-[12px] font-semibold" style={{ color: SOFT.ink }}>
        {[dayText(t, day.label), day.date, day.city].filter(Boolean).join(" · ")}
      </span>
      <span className="shrink-0 text-[11px]" style={{ color: SOFT.body }}>
        {t("ai_concierge.itinerary_stops", { defaultValue: "{{count}} stops", count: stops })}
      </span>
    </div>
  );
}

/**
 * The day tabs: a white control with one hairline, and one navy highlight
 * that slides to the chosen day by transform. Arrow keys move between days,
 * in reading order (so they flip in Arabic).
 */
function DayTabs({
  days,
  value,
  onChange,
  idBase,
  label,
}: {
  days: ItineraryDay[];
  value: number;
  onChange: (i: number) => void;
  idBase: string;
  label: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const { t } = useTranslation();
  const n = days.length;

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const rtl = (e.currentTarget.closest("[dir]")?.getAttribute("dir") ?? document.dir) === "rtl";
    const fwd = rtl ? "ArrowLeft" : "ArrowRight";
    const back = rtl ? "ArrowRight" : "ArrowLeft";
    let next = value;
    if (e.key === fwd) next = (value + 1) % n;
    else if (e.key === back) next = (value - 1 + n) % n;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = n - 1;
    else return;
    e.preventDefault();
    onChange(next);
    refs.current[next]?.focus();
  };

  return (
    <div className="-mx-1 mt-2.5 overflow-x-auto px-1 py-1 [scrollbar-width:none]">
      <div
        role="tablist"
        aria-label={label}
        onKeyDown={onKey}
        className="relative grid rounded-full p-1 [--mj-dir:1] rtl:[--mj-dir:-1]"
        style={{
          ...CONTROL_STYLE,
          gridTemplateColumns: `repeat(${n}, minmax(64px, 1fr))`,
        }}
      >
        <span
          aria-hidden="true"
          data-testid="itinerary-tab-highlight"
          className="mj-hl absolute inset-y-1 rounded-full"
          style={{
            ...CHOSEN_STYLE,
            insetInlineStart: 4,
            width: `calc((100% - 8px) / ${n})`,
            transform: `translateX(calc(${value} * 100% * var(--mj-dir)))`,
          }}
        />
        {days.map((d, i) => {
          const on = i === value;
          return (
            <button
              key={`${d.label}-${i}`}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`${idBase}-tab-${i}`}
              aria-selected={on}
              aria-controls={`${idBase}-panel`}
              tabIndex={on ? 0 : -1}
              onClick={() => onChange(i)}
              className="mj-hl-text relative z-[1] min-h-[32px] whitespace-nowrap px-2 text-[12.5px] font-semibold"
              style={{ color: on ? "#fff" : SOFT.ink }}
            >
              {dayText(t, d.label)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Timeline({
  day,
  matchBySlug,
  lang,
}: {
  day: ItineraryDay;
  matchBySlug: Map<string, AiMatch>;
  lang: string;
}) {
  const { t } = useTranslation();
  // Only stops we can draw take a number, so the numbers never skip.
  const stops = day.slots
    .map((s) => ({ slot: s, match: s.matchSlug ? matchBySlug.get(s.matchSlug) : undefined }))
    .filter(({ slot, match }) => match || slot.place);

  return (
    <ol className="flex flex-col">
      {stops.map(({ slot, match }, i) => {
        const last = i === stops.length - 1;
        const title = match ? (match.title ?? match.slug) : slot.place!.title;
        // The plan's own line wins: it says why this stop is here, which the
        // place's kind and city cannot.
        const note = match
          ? slot.note ?? [kindLabel(t, match), match.cityName].filter(Boolean).join(" · ")
          : slot.place?.note;
        const body = (
          <>
            <span
              className="block text-[10.5px] font-semibold uppercase tracking-[0.06em]"
              style={{ color: SOFT.body }}
            >
              {slotText(t, slot.time)}
            </span>
            <span className="block text-[13px] font-semibold leading-[18px]" style={{ color: SOFT.ink }}>
              {title}
            </span>
            {note ? (
              <span className="block text-[11.5px]" style={{ color: SOFT.body }}>
                {note}
              </span>
            ) : null}
          </>
        );
        return (
          <li key={`${title}-${i}`} className="flex gap-3">
            <span className="flex flex-col items-center">
              <span
                className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[10.5px] font-bold tabular-nums"
                style={i === 0 ? { background: SOFT.inkGradient, color: SOFT.gold, boxShadow: "0 8px 16px -10px rgba(17,14,57,0.8)" } : { ...CONTROL_STYLE, color: SOFT.ink }}
              >
                {i + 1}
              </span>
              {!last ? (
                <span className="w-px flex-1" style={{ background: SOFT.line, minHeight: 14 }} />
              ) : null}
            </span>
            <span className="min-w-0 flex-1 pb-3">
              {/* The travel note sits where the guest is between two stops:
                  above the stop it leads to. */}
              {slot.transit && i > 0 ? (
                <span
                  className="mb-1 flex items-center gap-1 text-[11px]"
                  style={{ color: SOFT.body }}
                  data-testid="itinerary-transit"
                >
                  <Icon name="ri-route-line" size={11} aria-hidden="true" />
                  {slot.transit}
                </span>
              ) : null}
              {match ? (
                <a
                  href={activityHrefString({
                    lang,
                    citySlug: match.citySlug,
                    categorySlug: match.categorySlug,
                    segment: match.segment,
                    slug: match.slug,
                  })}
                  className="flex min-h-[44px] items-center gap-2.5 rounded-[10px] outline-none focus-visible:ring-2 focus-visible:ring-[#110E39]"
                >
                  <span className="min-w-0 flex-1">{body}</span>
                  {match.heroImageUrl ? (
                    <span
                      aria-hidden="true"
                      className="h-10 w-10 shrink-0 rounded-[9px]"
                      style={{ background: `center / cover no-repeat url(${match.heroImageUrl})` }}
                    />
                  ) : null}
                  <Icon
                    name="ri-arrow-right-up-line"
                    size={14}
                    className="shrink-0 rtl:-scale-x-100"
                    style={{ color: SOFT.body }}
                    aria-hidden="true"
                  />
                </a>
              ) : (
                <div>{body}</div>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** "Add all to my trip", with the existing new-or-active chooser. */
function AddToTrip({
  onAddToTrip,
  activeTripName,
}: {
  onAddToTrip: (mode: "new" | "active") => void;
  activeTripName?: string | null;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const item =
    "flex min-h-[40px] w-full items-center gap-2 px-3 text-start text-[12.5px] font-medium hover:bg-[rgba(17,14,57,0.04)]";

  return (
    <div className="relative mt-1" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="mj-press flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-full text-[12.5px] font-semibold"
        style={{ ...CONTROL_STYLE, color: SOFT.ink }}
      >
        <Icon name="ri-add-line" size={15} aria-hidden="true" />
        {t("trip.add_itinerary")}
      </button>
      {open ? (
        <div
          role="menu"
          className="mj-drop-in absolute end-0 z-10 mt-1 w-56 overflow-hidden rounded-[14px] bg-white py-1"
          style={{ boxShadow: `0 0 0 1px ${SOFT.controlEdge}, 0 12px 28px rgba(17,14,57,0.14)` }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onAddToTrip("new");
            }}
            className={item}
            style={{ color: SOFT.ink }}
          >
            <Icon name="ri-add-circle-line" aria-hidden="true" />
            {t("trip.add_as_new")}
          </button>
          {activeTripName ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onAddToTrip("active");
              }}
              className={item}
              style={{ color: SOFT.ink }}
            >
              <Icon name="ri-suitcase-line" aria-hidden="true" />
              {t("trip.add_to_active", { name: activeTripName })}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
